import React, { Component } from "react";
import { Form, Button, Col, Row, Input, Select, message as messagePopup, InputNumber, Divider } from "antd";
import { connect } from "dva";
import { getTAQQuote } from "@/services/quotes";

const { Option } = Select;
const { TextArea } = Input;

class PricingEngineFeedbackForm extends Component {
  state = {
    dealerReason: 0,
    transporterReason: 0,
    followingUp: false,
  };

  onDealerReasonChange = value => {
    this.setState({
      dealerReason: value,
    });
  };

  onTransporterReasonChange = value => {
    this.setState({
      transporterReason: value,
    });
  };

  calcTransporterPrice = quote => {
    if (!quote.Results) return 0;
    const { deposit, total } = quote.Results;
    if (!deposit || !total) return 0;

    return Math.ceil(total - deposit);
  };

  calcDealerPrice = transporterPrice => Math.ceil(transporterPrice / (1 - 0.18)); // Margin of 18%

  getPricingEngine = async () => {
    const {
      vehicleFormRef,
      vehicleShipmentData,
      userManager,
      currentProfile,
      setParentState,
      pricingEngineFeedBackFormRef,
      checkUpdatedPrices,
      vehicleInfo,
    } = this.props;

    // set loading state
    setParentState({
      loadingPricingEngineResponse: true,
      usedPricingEngine: true,
    });

    let vehicleData;
    let quote;
    let quoteError;

    // validate vehicle form to ensure vehicle data can be added to quote request
    if (vehicleFormRef) {
      try {
        vehicleData = await vehicleFormRef.current.validateFields();
      } catch (err) {
        setParentState({
          loadingPricingEngineResponse: false,
          usedPricingEngine: false,
        });

        return;
      }
    } else {
      // no vehicle form passed, check vehicleInfo prop instead

      vehicleData = {
        year: vehicleInfo.year,
        make: vehicleInfo.make,
        model: vehicleInfo.model,
        enclosedTransport: vehicleInfo.enclosed,
        inoperational: vehicleInfo.inoperational,
      };
    }

    // get vehicle data for request to taq api
    const payload = {
      Transport: {
        Carrier: vehicleData.enclosedTransport ? "Enclosed" : "Open",
        Origin: {
          City: vehicleShipmentData.pickup.location.city.replace(/'/g, "\\'"),
          State: vehicleShipmentData.pickup.location.state,
          Zipcode: vehicleShipmentData.pickup.location.zip,
        },
        Destination: {
          City: vehicleShipmentData.dropoff.location.city.replace(/'/g, "\\'"),
          State: vehicleShipmentData.dropoff.location.state,
          Zipcode: vehicleShipmentData.dropoff.location.zip,
        },
        Vehicles: [
          {
            v_year: vehicleData.year,
            v_make: vehicleData.make,
            v_model: vehicleData.model,
            veh_op: vehicleData.inoperational ? 0 : 1,
          },
        ],
      },
    };

    // call taq api and get quote data
    try {
      quote = await getTAQQuote(userManager, payload);
    } catch (err) {
      quoteError = err;

      // check for non-json error response
      if (
        typeof quoteError === "string" &&
        quoteError ===
          "VEHICLE NOT FOUND IN YOUR VEHICLE SIZE DATABASE.  PLEASE SET A SIZE FOR THIS VEHICLE IN YOUR VEHICLE SIZE QUEUE."
      ) {
        messagePopup.error("Vehicle not found in vehicle size database.");
      } else {
        messagePopup.error("Error quoting vehicle.");
      }

      setParentState({
        loadingPricingEngineResponse: false,
        askingForFeedback: false,
      });
    }

    // if quote is defined at this point, then api request was successful, otherwise it failed

    let feedbackFormFillPayload = {
      userId: currentProfile.id,
      status: null,
      isError: false,
      errors: null,
      message: null,
      results: null,
      adjustments: null,
      quoteId: null,
      encryptedQuoteId: null,
      deposit: null,
      total: null,
      mileage: null,
      transporterPrice: null,
      dealerPrice: null,
      notes: "",
    };

    if (quote) {
      // parse out data for display from quote
      const transporterPrice = this.calcTransporterPrice(quote);
      const dealerPrice = this.calcDealerPrice(transporterPrice);
      const status = quote.Status;
      const isError = status !== 1001;
      const errors = JSON.stringify(quote.Errors);
      const message = quote.Message;
      const results = JSON.stringify(quote.Results);
      const adjustments = JSON.stringify(quote.Results.adjustments);
      const { quote_id: quoteId, encrypted_quote_id: encryptedQuoteId, deposit, total, mileage } = quote.Results;

      feedbackFormFillPayload = {
        ...feedbackFormFillPayload,
        transporterPrice,
        dealerPrice,
        status,
        isError,
        errors,
        message,
        results,
        adjustments,
        quoteId,
        encryptedQuoteId,
        deposit,
        total,
        mileage,
      };
    } else {
      // parse out error data for feedback submission
      const isError = true;
      const status = quoteError.Status ? quoteError.Status : null;
      const errors = quoteError.Errors ? JSON.stringify(quoteError.Errors) : JSON.stringify(quoteError);
      const message = quoteError.Message ? quoteError.Message : null;
      const results = quoteError.Results ? JSON.stringify(quoteError.Results) : null;

      feedbackFormFillPayload = {
        ...feedbackFormFillPayload,
        isError,
        status,
        errors,
        message,
        results,
      };

      // mileage may be set to an error value in results when call fails
      // possible some other values we want to store are set to error values in other error responses
      if (quoteError.Results) {
        for (let key in quoteError.Results) {
          feedbackFormFillPayload[key] = quoteError.Results[key];
        }
      }
    }

    // fill form fields with quote data
    pricingEngineFeedBackFormRef.current.setFieldsValue({
      ...feedbackFormFillPayload,
    });

    // run to evaluate if suggested prices are being used
    checkUpdatedPrices();

    setParentState({
      loadingPricingEngineResponse: false,
      askingForFeedback: quoteError ? false : true,
      taqSuccess: quoteError ? false : true,
    });
  };

  useSuggestedPrices = () => {
    const {
      vehicleFormRef,
      pricingEngineFeedBackFormRef,
      quotesFormRef,
      setParentState,
      autoSubmitCallBack,
      automatedPricingIsSafe,
    } = this.props;
    const suggestedDealerPrice = pricingEngineFeedBackFormRef?.current?.getFieldValue("dealerPrice");
    const suggestedTransporterPrice = pricingEngineFeedBackFormRef?.current?.getFieldValue("transporterPrice");

    // return early if couldn't get prices from suggestion form fields
    if (!suggestedDealerPrice || !suggestedTransporterPrice) return;

    // check for name of form ref to autofill
    let formRef = vehicleFormRef ? vehicleFormRef : quotesFormRef;

    formRef.current.setFieldsValue({
      dealerPrice: suggestedDealerPrice,
      transporterPrice: suggestedTransporterPrice,
    });

    setParentState(
      {
        usingSuggestedDealerPrice: true,
        usingSuggestedTransporterPrice: true,
        currentDealerPrice: suggestedDealerPrice,
        currentTransporterPrice: suggestedTransporterPrice,
        dealerPrice: suggestedDealerPrice,
        transporterPrice: suggestedTransporterPrice,
      },
      () => {
        if (autoSubmitCallBack && automatedPricingIsSafe) {
          // auto submit prices if callback provided and safe to use price as is
          autoSubmitCallBack?.();
        }
      },
    );
  };

  componentDidUpdate() {
    if (this.props.loadingPricingEngineResponse) return;
    if (!this.props.displayPricingEngine) return;

    if (this.props.automatedPricingIsSafe && !this.props.usedPricingEngine) {
      this.getPricingEngine()
        .then(() => {
          this.useSuggestedPrices();
        })
        .catch(() => {
          console.log("error when getting pricing engine");
        });
    }
  }

  render() {
    const {
      vinValidated,
      usedPricingEngine,
      loadingPricingEngineResponse,
      askingForFeedback,
      pricingEngineFeedBackFormRef,
      usingSuggestedDealerPrice,
      usingSuggestedTransporterPrice,
      automatedPricingIsSafe,
      checkingAutomatedPricing,
      configPerm,
    } = this.props;

    const { dealerReason, transporterReason } = this.state;

    return (
      <>
        {/* Option to use pricing engine only shows up for top level autosled admins at the moment */}
        {configPerm["PricingService.PricingEngineFeedbacks"] && vinValidated && (
          <>
            {/* <AutoPricingAlert
              automatedPricingIsSafe={automatedPricingIsSafe}
              checkingAutomatedPricing={checkingAutomatedPricing}
              useSuggestedPrices={this.useSuggestedPrices}
              pricingEngineFeedBackFormRef={pricingEngineFeedBackFormRef}
              rulesEngineErrors={rulesEngineErrors}
              rulesEngineResults={rulesEngineResults}
              taqSuccess={taqSuccess}
              loadingPricingEngineResponse={loadingPricingEngineResponse}
            /> */}
            <Row gutter={16}>
              <Button
                type="primary"
                style={
                  checkingAutomatedPricing || automatedPricingIsSafe
                    ? { display: "none" }
                    : {
                        marginTop: 16,
                        marginBottom: 10,
                      }
                }
                onClick={this.getPricingEngine}
                loading={loadingPricingEngineResponse}
                disabled={(!loadingPricingEngineResponse && usedPricingEngine) || checkingAutomatedPricing}
              >
                Use Autosled Pricing Engine
              </Button>
            </Row>
          </>
        )}

        {/* Form below mounts if pricing engine button is clicked */}
        {/* Form visibility is controlled by whether or not a quote could be generated. In the case of failure,
        the form still needs to be mounted to add error data into POST request to store new TAQResult on backend,
        but no need for user to enter any of that data into form or see it. So using askingForFeedback state variable
        to set whether the form is visible or not. */}

        {usedPricingEngine && (
          <>
            {askingForFeedback && <Divider />}

            <Form
              ref={pricingEngineFeedBackFormRef}
              layout="vertical"
              style={askingForFeedback ? { display: "unset" } : { display: "none" }}
              initialValues={{ dealerReason: 0, transporterReason: 0 }}
            >
              <Form.Item name="userId" style={{ display: "none" }}>
                <Input />
              </Form.Item>
              <Form.Item name="status" style={{ display: "none" }}>
                <Input />
              </Form.Item>
              <Form.Item name="message" style={{ display: "none" }}>
                <Input />
              </Form.Item>
              <Form.Item name="results" style={{ display: "none" }}>
                <Input />
              </Form.Item>
              <Form.Item name="adjustments" style={{ display: "none" }}>
                <Input />
              </Form.Item>
              <Form.Item name="quoteId" style={{ display: "none" }}>
                <Input />
              </Form.Item>
              <Form.Item name="encryptedQuoteId" style={{ display: "none" }}>
                <Input />
              </Form.Item>
              <Form.Item name="deposit" style={{ display: "none" }}>
                <Input />
              </Form.Item>
              <Form.Item name="total" style={{ display: "none" }}>
                <Input />
              </Form.Item>
              <Form.Item name="mileage" style={{ display: "none" }}>
                <Input />
              </Form.Item>
              <Form.Item name="isError" style={{ display: "none" }}>
                <Input />
              </Form.Item>
              <Form.Item name="errors" style={{ display: "none" }}>
                <Input />
              </Form.Item>
              <Row gutter={16}>
                <Col lg={12}>
                  <Form.Item
                    name="dealerPrice"
                    label="Suggested Dealer Price"
                    style={automatedPricingIsSafe && { display: "none" }}
                  >
                    <InputNumber
                      min={0}
                      style={{ width: "100%" }}
                      formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                      parser={value => value.replace(/\$\s?|(,*)/g, "")}
                      readOnly
                    />
                  </Form.Item>
                </Col>
                <Col lg={12}>
                  <Form.Item
                    name="transporterPrice"
                    label="Suggested Transporter Price"
                    style={automatedPricingIsSafe && { display: "none" }}
                  >
                    <InputNumber
                      min={0}
                      style={{ width: "100%" }}
                      formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                      parser={value => value.replace(/\$\s?|(,*)/g, "")}
                      readOnly
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Button
                  type="secondary"
                  style={{
                    marginBottom: 10,
                  }}
                  onClick={this.useSuggestedPrices}
                >
                  Use Suggested Prices
                </Button>
              </Row>
              <Row gutter={16}>
                <Col lg={12}>
                  <Form.Item
                    name="dealerReason"
                    label="Dealer Estimate Feedback"
                    rules={[
                      {
                        required: askingForFeedback && !usingSuggestedDealerPrice,
                        validator: (_, value) => {
                          if (askingForFeedback && !usingSuggestedDealerPrice && value === 0) {
                            return Promise.reject("Price must be within 25 dollars to be considered reasonable.");
                          }

                          return Promise.resolve();
                        },
                      },
                    ]}
                    style={automatedPricingIsSafe && { display: "none" }}
                  >
                    <Select
                      value={dealerReason}
                      onChange={this.onDealerReasonChange}
                      getPopupContainer={trigger => trigger.parentElement}
                    >
                      <Option value={0}>Looks reasonable</Option>
                      <Option value={1}>Think it is wrong</Option>
                      <Option value={2}>Market forces</Option>
                      <Option value={3}>Bulk deal</Option>
                      <Option value={4}>Loss leader</Option>
                      <Option value={5}>Negotiated route</Option>
                      <Option value={6}>Other</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col lg={12}>
                  <Form.Item
                    name="transporterReason"
                    label="Transporter Estimate Feedback"
                    rules={[
                      {
                        required: askingForFeedback && !usingSuggestedTransporterPrice,
                        validator: (_, value) => {
                          if (askingForFeedback && !usingSuggestedTransporterPrice && value === 0) {
                            return Promise.reject("Price must be within 25 dollars to be considered reasonable.");
                          }

                          return Promise.resolve();
                        },
                      },
                    ]}
                    style={automatedPricingIsSafe && { display: "none" }}
                  >
                    <Select
                      value={transporterReason}
                      onChange={this.onTransporterReasonChange}
                      getPopupContainer={trigger => trigger.parentElement}
                    >
                      <Option value={0}>Looks reasonable</Option>
                      <Option value={1}>Think it is wrong</Option>
                      <Option value={2}>Market forces</Option>
                      <Option value={3}>Bulk deal</Option>
                      <Option value={4}>Loss leader</Option>
                      <Option value={5}>Negotiated route</Option>
                      <Option value={6}>Other</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col lg={24}>
                  <Form.Item name="notes" label="Estimate Notes" style={automatedPricingIsSafe && { display: "none" }}>
                    <TextArea rows={4} />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </>
        )}
      </>
    );
  }
}

export default connect(({ user, adminShipmentView, quotesView, configs }) => ({
  rulesEngineErrors: window.location.href.includes("shipment")
    ? adminShipmentView?.rulesEngineErrors
    : quotesView?.rulesEngineErrors,
  rulesEngineResults: window.location.href.includes("shipment")
    ? adminShipmentView?.rulesEngineResults
    : quotesView?.rulesEngineResults,
  userManager: user.userManager,
  currentProfile: user.currentProfile,
  configPerm: configs.configPerm,
}))(PricingEngineFeedbackForm);
